import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Shield } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Stars, StarInput } from "@/components/StarInput";

export function RatingsPanel({ title, summary, setSummary, mine, setMine }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const rate = async (v) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/titles/${title.id}/rate`, { value: v });
      setSummary(data.summary); setMine(v);
      toast.success(data.updated ? `Rating updated to ${v}★` : `You rated ${title.title} ${v}★`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const max = Math.max(1, ...Object.values(summary.distribution));
  const v = title.verdict || {};
  return (
    <section id="ratings" data-testid="ratings-panel" className="grid gap-4 lg:grid-cols-3">
      <div className="rounded-2xl border border-[#F5C518]/20 bg-[#12121A] p-5" data-testid="imdb-rating-box">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#F5C518]">IMDb rating · external</p>
        {title.imdb_rating ? (
          <><p className="mt-2 font-display text-5xl text-[#F5C518]">{title.imdb_rating}<span className="text-xl text-slate-500">/10</span></p>
            <p className="text-xs text-slate-500">{title.imdb_votes ? `${title.imdb_votes.toLocaleString()} votes · ` : ""}Source: {title.imdb_source}</p></>
        ) : <p className="mt-3 text-lg font-semibold text-slate-400">IMDb rating unavailable</p>}
        {title.imdb_id && <a href={`https://www.imdb.com/title/${title.imdb_id}/`} target="_blank" rel="noopener noreferrer" data-testid="imdb-link" className="mt-3 inline-block text-xs font-semibold text-[#F5C518] hover:underline">View on IMDb ({title.imdb_id}) →</a>}
      </div>
      <div className="rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-400/10 to-[#12121A] p-5" data-testid="verdict-box">
        <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-amber-400"><Shield className="h-3.5 w-3.5" />Shivam Cine Core Verdict</p>
        {v.label || v.text ? (
          <>
            <p className="mt-2 font-display text-4xl text-amber-300">{v.label}{v.score ? <span className="ml-2 text-2xl text-slate-400">{v.score}/10</span> : null}</p>
            {v.text && <p className="mt-2 text-sm italic text-slate-300">“{v.text}”</p>}
          </>
        ) : <p className="mt-3 text-lg font-semibold text-slate-400">Verdict coming soon</p>}
        <p className="mt-2 text-[11px] text-slate-500">Editorial opinion — not a user rating.</p>
      </div>
      <div className="rounded-2xl border border-purple-400/30 bg-gradient-to-br from-purple-600/10 to-[#12121A] p-5" data-testid="community-rating-box">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-purple-300">Community rating</p>
        {summary.count ? (
          <div className="mt-2 flex items-end gap-3">
            <p className="font-display text-5xl text-purple-300" data-testid="community-average">{summary.average.toFixed(1)}<span className="text-xl text-slate-500">/5</span></p>
            <div className="pb-2"><Stars value={summary.average} size={14} /><p className="text-xs text-slate-500" data-testid="community-count">{summary.count} rating{summary.count === 1 ? "" : "s"}</p></div>
          </div>
        ) : <p className="mt-3 text-lg font-semibold text-slate-400" data-testid="not-rated-yet">Not rated yet</p>}
        <div className="mt-3 space-y-1">
          {[5, 4, 3, 2, 1].map((s) => (
            <div key={s} className="flex items-center gap-2 text-[11px] text-slate-500">
              <span className="w-3">{s}</span>
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/5"><div className="h-full rounded-full bg-purple-500" style={{ width: `${(summary.distribution[s] / max) * 100}%` }} /></div>
              <span className="w-5 text-right">{summary.distribution[s]}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 border-t border-white/5 pt-4" id="rate">
          <p className="mb-1 text-xs text-slate-400">{mine ? "Your rating (tap to change)" : "Rate this title"}</p>
          {user ? <StarInput value={mine} onChange={rate} disabled={busy} />
            : <Link to={`/login?next=${encodeURIComponent(window.location.pathname)}`} data-testid="rate-signin" className="text-sm font-semibold text-purple-300 hover:text-purple-200">Sign in to rate →</Link>}
        </div>
      </div>
    </section>
  );
}
